/* ============================================================
   MarketBetter — Customer Stories data (single source of truth)
   Both the listing (cards + featured carousel) and the detail
   pages derive from CASE_STUDIES, so they can never drift.

   These stories are grounded in real customer engagements.
   ============================================================ */

export type ProductLine = "sales" | "marketing";

export interface ResultMetric {
  value: string;
  label: string;
  accent?: boolean;
}

export interface StorySection {
  id: string;
  kicker: string;
  title: string;
  body: string[];
  pullquote?: string;
  pullquoteBy?: string;
  bodyAfter?: string[];
}

export interface CaseStudy {
  slug: string;
  line: ProductLine;
  customer: string;
  industry: string;

  /** Appears in the featured carousel when true (top of listing). */
  featured?: boolean;

  /** Hidden from the listing, carousel, and detail routes (e.g. pending customer sign-off). */
  hidden?: boolean;

  /** Listing card. */
  cardTitle: string;
  stat: string;
  statLabel: string;
  readTime: string;

  /** Detail hero. */
  headlinePre: string;
  headlineHi: string;
  headlinePost: string;
  dek: string;

  /** Byline. */
  author: string;
  authorTitle: string;
  authorInitials: string;
  /** Optional byline headshot URL; falls back to initials. */
  authorImage?: string;
  date: string;

  /** Customer quote (carousel + detail hero card). */
  quote: string;
  quoteAuthor: string;
  quoteTitle: string;
  quoteInitials: string;
  /** Optional customer headshot URL; falls back to initials. */
  quoteImage?: string;

  /** Floating stat badge on the detail hero. */
  statBadge: { value: string; label: string };

  results: ResultMetric[];
  atAGlance: [string, string][];
  sections: StorySection[];
}

export const CASE_STUDIES: Record<string, CaseStudy> = {
  "hologram": {
    "slug": "hologram",
    "line": "sales",
    "customer": "Hologram",
    "industry": "IoT Cellular Connectivity",
    "cardTitle": "Hologram built $4M+ in new opportunity from email and LinkedIn sequences",
    "stat": "$4M+",
    "statLabel": "in new opportunity from email and LinkedIn sequences",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "Hologram built $4M+ in new opportunity from email and LinkedIn sequences",
    "headlinePost": "",
    "dek": "Hologram wanted its BDRs spending their time in conversations with buyers. MarketBetter spots the accounts showing interest, researches them, sends each one to the right regional BDR and drafts the email and LinkedIn sequences. Hologram credits those sequences with over $4M in new opportunity.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "Between our email and LinkedIn sequences, MarketBetter has created over $4M in new opportunity for us. Every BDR gets a task the moment a signal fires — the system does the research, my team does the selling.",
    "quoteAuthor": "Jonathan Rosenfeld",
    "quoteTitle": "Head of Marketing, Hologram",
    "quoteInitials": "JR",
    "quoteImage": "/images/testimonials/jonathan_rosenfeld.jpeg",
    "statBadge": {
      "value": "$4M+",
      "label": "in opportunity from outbound sequences"
    },
    "results": [
      {
        "value": "$4M+",
        "label": "in new opportunity created via MarketBetter email and LinkedIn sequences",
        "accent": true
      },
      {
        "value": "55",
        "label": "replies from a single 2,000-contact outbound campaign"
      },
      {
        "value": "2,080",
        "label": "champions tracked in Salesforce with job-change alerts"
      },
      {
        "value": "3 regions",
        "label": "covered by AI-routed task assignment"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "Hologram"
      ],
      [
        "Industry",
        "IoT Cellular Connectivity"
      ],
      [
        "Team",
        "Full GTM org — BDRs, AEs, Marketing, RevOps"
      ],
      [
        "Using",
        "Signals, Tasks, Audiences, Dialer, Champion Tracking, Salesforce Sync"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Selling IoT connectivity to very different buyers",
        "body": [
          "Hologram sells cellular connectivity for IoT devices deployed around the world. Its customers range from fleet operators and agricultural technology companies to industrial manufacturers and EV charging businesses. To pitch any of them well, a rep has to know what the company is building and where it operates.",
          "The sales team covers the US, EMEA and other regions. Marketing, BDRs, account executives and RevOps all share that territory, and Salesforce holds the account and opportunity records."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Plenty of tools, and reps still stitching them together",
        "body": [
          "Hologram already had an outbound stack: Clay for lists, SmartLead for email delivery, Warmly for visitor identification and Gong Engage for sequences. Someone also had to keep nearly 20 sending domains running.",
          "None of it came together around a single account. A website visit showed up in one tool, while contact research and outreach sat in others. Jonathan Rosenfeld, who leads marketing, wanted his sellers spending their time in conversations and less of it gathering what they needed to start one."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Website interest becomes a task for the right regional BDR",
        "body": [
          "It starts with MarketBetter’s tracking script. When someone visits the site, the team sees the company, any person-level detail available and the pages they read. Intent analysis sorts out which visits are worth acting on.",
          "When an ICP account shows up, MarketBetter creates a task for the BDR who owns that territory: EMEA, US and Canada, or the rest of the world. The task arrives with enriched contact details and a drafted sequence, so the rep knows what to do next.",
          "The team also connected Salesforce for champion tracking. MarketBetter found 2,080 people who had championed past deals and watches for job changes. When one of them moves, the seller has a good reason to get back in touch."
        ],
        "pullquote": "One of our reps got a positive reply fourteen minutes into my SKO presentation — from a sequence running in the background while he sat in the room.",
        "pullquoteBy": "Jonathan Rosenfeld · Head of Marketing, Hologram"
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "The same process for outbound lists, calls and events",
        "body": [
          "BDRs build outbound audiences from uploaded lists, CRM imports and Sales Navigator searches pulled in through the Chrome extension. MarketBetter researches each prospect and writes personalized email sequences. One campaign aimed at rural mobile operators went to about 2,000 contacts and got 55 replies.",
          "Calling works the same way, with prepared scripts, call scoring and follow-up tasks. The team also used exhibitor lists to book meetings around conferences it didn’t attend in person."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Each task tells the rep who to contact and why",
        "body": [
          "When a task lands, the rep sees the account background, why it’s worth reaching out now and a draft message to review. Regional routing settles who owns it. Salesforce still keeps the account history and the opportunities.",
          "The whole team adopted it. At Hologram’s sales kickoff (SKO), Jonathan presented MarketBetter as part of the GTM strategy. While he was presenting, a sequence running in the background landed a positive reply for a rep sitting in the audience."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "Over $4M in new opportunity, and a process the team repeats",
        "body": [
          "Hologram reports **over $4M in new opportunity** from MarketBetter email and LinkedIn sequences. The figure measures opportunity value in the pipeline.",
          "Two numbers show the work behind it. One campaign to about 2,000 contacts drew 55 replies, and the 2,080 tracked champions keep giving the team people worth calling again.",
          "Inbound follow-up, outbound campaigns and champion alerts now run through one place. When marketing flags an account, the BDR gets it with the research done, the owner set and the outreach drafted."
        ]
      }
    ],
    "featured": true,
    "hidden": false
  },
  "kajeet": {
    "slug": "kajeet",
    "line": "sales",
    "customer": "Kajeet",
    "industry": "IoT Connectivity",
    "cardTitle": "Kajeet turned website visits into 14 new enterprise opportunities in its first quarter",
    "stat": "14",
    "statLabel": "new opportunities sourced from website signal in the first quarter",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "Kajeet turned website visits into 14 new enterprise opportunities in its first quarter",
    "headlinePost": "",
    "dek": "Kajeet’s marketing was already bringing the right accounts to its website. What it lacked was a way to get each visit to the right rep. MarketBetter routed that interest to six SDRs and BDRs by territory, and signal-sourced outreach produced 14 new enterprise opportunities in the first quarter.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "This is awesome — this is exactly what I need. Right now I have to check three different places just to get the information MarketBetter puts in one task.",
    "quoteAuthor": "Juan Dewendt",
    "quoteTitle": "Enterprise BDR, Kajeet",
    "quoteInitials": "JD",
    "quoteImage": "/images/testimonials/juan_dewendt.jpeg",
    "statBadge": {
      "value": "14",
      "label": "new opportunities in the first quarter"
    },
    "results": [
      {
        "value": "14",
        "label": "new enterprise opportunities sourced from website signal in the first quarter",
        "accent": true
      },
      {
        "value": "91%",
        "label": "open rate on the first AI-personalized campaign wave"
      },
      {
        "value": "6",
        "label": "SDRs and BDRs onboarded with territory-routed signal queues"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "Kajeet"
      ],
      [
        "Industry",
        "IoT Connectivity"
      ],
      [
        "Team",
        "~33 reps; pilot started with 1 SDR"
      ],
      [
        "Using",
        "Signals, SDR Playbooks, Emails, Visitor ID"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Different buyers in every market",
        "body": [
          "Kajeet sells connectivity to enterprise IoT buyers, school districts, transit agencies and public-sector organizations. Each of those markets has its own decision-makers and its own coverage rules, so one big prospect list was never going to work for the sales team.",
          "When Kajeet started with MarketBetter, it had a $21M rolling pipeline and a lean sales team. Its marketing programs were already pulling interested accounts to the site. The goal was to turn more of that interest into sales conversations."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Interested accounts weren’t reaching the right rep",
        "body": [
          "Kajeet used Demandbase to prioritize accounts, and Salesforce was connected to HubSpot. Reps still had to check ZoomInfo, look through sequences and decide for themselves whom to contact. Many companies researching the website were never identified at all.",
          "Then the paid-media director left, and the gap got wider. Google Ads, LinkedIn and organic search kept sending visitors to the site, but nobody had a reliable way to turn a relevant visit into a specific SDR’s next task."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Website behavior creates a task for the right rep",
        "body": [
          "Kajeet started with Visitor ID and Signals, combining Demandbase enrichment with MarketBetter’s intent scoring. A visit to the contact page, or activity across at least three pages, kicked off prospect identification and research.",
          "Each task came with a relevant contact, background on the account and a personalized draft. Enterprise IoT tasks went to Juan Dewendt. K–12 and public-sector tasks went to five BDRs, split by geography and student population. Existing Salesforce customers were kept out of prospecting."
        ],
        "pullquote": "We were getting traffic from LinkedIn, Google, and ChatGPT referrals — but with no way to connect that to sales. MarketBetter made it one system.",
        "pullquoteBy": "Ashley Samuels · Director of Marketing, Kajeet"
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "Everything logs back to Salesforce",
        "body": [
          "Emails, calls and completed tasks synced back to Salesforce, with line-of-business fields filled in automatically. Every call and email stayed tied to its account and segment."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Each rep starts the day with their own territory",
        "body": [
          "Reps opened a prioritized list for their own coverage area. Nobody had to carve the full marketing account list into a personal research plan before reaching out.",
          "Marketing could now trace the whole path. A campaign brought an account to the site, what it did there supplied the context, and the territory owner got the task. For the SDR, the research and the draft message arrived with the task. Getting a new rep up and running took about 20 minutes."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "14 opportunities, and six reps working the same way",
        "body": [
          "Outbound driven by website signals produced **14 new enterprise opportunities in the first quarter**. The first personalized campaign wave hit a 91% open rate, though the opportunities are the number that counts.",
          "The rollout grew from one enterprise BDR to six reps, each with a territory-based signal queue. Enterprise, education and public-sector accounts now run through the same process, and each still gets routed its own way.",
          "A campaign brings an account to the site, the right rep gets the task, and the calls and emails that follow land in Salesforce."
        ]
      }
    ],
    "featured": true,
    "hidden": false
  },
  "ecoonline": {
    "slug": "ecoonline",
    "line": "sales",
    "hidden": false,
    "customer": "EcoOnline",
    "industry": "EHS & Safety Software",
    "cardTitle": "EcoOnline closed $350K+ in business by putting researched accounts in front of its BDRs",
    "stat": "$350K+",
    "statLabel": "closed business",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "EcoOnline closed $350K+ in business by putting researched accounts in front of its BDRs",
    "headlinePost": "",
    "dek": "EcoOnline’s BDRs were sitting on thousands of old leads with no shared way to decide who deserved another try. MarketBetter qualified and researched those contacts, picked up buying intent from the website and put the best accounts on each BDR’s Today page. The work has supported over $350K in closed business and new multi-product opportunities.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "",
    "quoteAuthor": "",
    "quoteTitle": "",
    "quoteInitials": "",
    "statBadge": {
      "value": "$350K+",
      "label": "closed business"
    },
    "results": [
      {
        "value": "$350K+",
        "label": "closed business",
        "accent": true
      },
      {
        "value": "Today page",
        "label": "prioritized accounts for BDR follow-up"
      },
      {
        "value": "Same morning",
        "label": "email reply, meeting and multi-product opportunity"
      },
      {
        "value": "120",
        "label": "qualified contacts selected from the original 1,900-contact pilot backlog"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "EcoOnline"
      ],
      [
        "Industry",
        "EHS & Safety Software"
      ],
      [
        "Team",
        "~40 BDRs across NAM, UKI, and Nordics"
      ],
      [
        "Using",
        "Audiences, Emails, Signals, Visitor ID, CRM Integration"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Safety software where the first conversation has to fit the buyer",
        "body": [
          "EcoOnline sells environmental, health and safety software, covering chemical safety, lone worker protection, crisis management and more. What a rep should open with depends on how the prospect’s operation runs and what the buyer is responsible for.",
          "About 40 BDRs covered North America, the UK and the Nordics, split between inbound and outbound. For any account strategy to work, reps in all three regions had to work accounts the same way."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Thousands of old leads and no agreed way to sort them",
        "body": [
          "The Nordics alone had piled up more than 5,600 marketing-qualified leads. Each rep researched accounts their own way, and nobody had a standard for deciding which contacts were worth reaching out to again.",
          "The volume-heavy cadences were hurting deliverability, too. Emailing old leads again and again ate up time without making the messages any more relevant. EcoOnline wanted to confirm a contact fit, research the account and have a useful message ready before handing it to a BDR."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Start with 1,900 leads and show the reasoning",
        "body": [
          "The pilot started in North America with 1,900 existing leads. MarketBetter checked each contact’s current role, read their LinkedIn profile and scored them against EcoOnline’s ICP and seniority rules. Every score came with reasoning the team could check.",
          "120 contacts made the cut. For those, MarketBetter researched each company (its website, hiring activity and public safety information) and built four-touch sequences around what that buyer was dealing with.",
          "The pilot looked at website behavior as well. Demo-page visits and interest across product pages picked out 84 high-intent accounts from about 43,000 visitors, one more input for deciding which accounts came first."
        ]
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "Plug the results into HubSpot, Salesforce and Gong",
        "body": [
          "The HubSpot integration handed the work off to the rest of EcoOnline’s sales tools. Sequence activity updated a custom field, which triggered Salesforce campaign membership and a Gong calling follow-up.",
          "From there the scope grew to closed-lost reactivation, 6sense account surges and matched ABM audiences. The 60-day pilot turned into an annual engagement, built on a qualification process the team could run again and again."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "The next accounts show up on the BDR’s Today page",
        "body": [
          "BDRs get a prioritized list of accounts to follow up with on their Today page. The research and prepared outreach arrive as work assigned to a specific rep, so marketing’s account plan becomes something a BDR actually does that day.",
          "Kyla Bell described what that can look like. A prospect replied to a MarketBetter email at 6 a.m. and asked to meet at 8:30. Kyla and her AE met with him that morning and came away with a multi-product opportunity.",
          "Once the email landed, it came down to people. Kyla and her AE answered quickly and worked through the buyer’s needs together. That opportunity is counted separately from the closed business below."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "Over $350K closed across enterprise accounts",
        "body": [
          "MarketBetter helped EcoOnline close **over $350K in business**. The accounts involved included a large industrial distributor and a large engineering and infrastructure firm.",
          "The pilot laid the groundwork: 120 qualified contacts picked from 1,900 leads, each with account research and a scoring rationale the team could read. A later Lone Worker campaign went to 127 contacts and got three positive replies, from buyers in public works, mechanical contracting and health and safety.",
          "The Today page puts that preparation into the BDR’s working day. Kyla’s same-morning meeting shows what happens when a relevant email reaches a buyer and a rep follows up fast: one reply turned into a conversation about several products."
        ]
      }
    ]
  },
  "vutility": {
    "slug": "vutility",
    "line": "sales",
    "customer": "Vutility",
    "industry": "Energy Monitoring Hardware",
    "cardTitle": "Vutility’s two-person sales team found 2,000+ ICP companies visiting its website",
    "stat": "2,000+",
    "statLabel": "ICP companies identified visiting their site",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "Vutility’s two-person sales team found 2,000+ ICP companies visiting its website",
    "headlinePost": "",
    "dek": "Vutility sells with two people and no marketing staff. MarketBetter identified the companies visiting its website, ran SDR calls and emails, and handed interested prospects to the founders.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "This lead alone is worth the monthly campaign cost.",
    "quoteAuthor": "Stephen Prince",
    "quoteTitle": "Co-Founder & President, Vutility",
    "quoteInitials": "SP",
    "quoteImage": "https://assets.marketbetter.ai/images/testimonials/stephen_prince.jpeg",
    "statBadge": {
      "value": "2,000+",
      "label": "ICP companies identified from site traffic"
    },
    "results": [
      {
        "value": "2,000+",
        "label": "ICP companies identified visiting the site",
        "accent": true
      },
      {
        "value": "493+",
        "label": "SDR calls made in first campaign month"
      },
      {
        "value": "300+",
        "label": "RV parks run by an investment firm that asked to talk further in the first month"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "Vutility"
      ],
      [
        "Industry",
        "Energy Monitoring Hardware"
      ],
      [
        "Team",
        "2-person sales team"
      ],
      [
        "Using",
        "Visitor ID, Managed SDR, Audiences, Emails"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Energy submetering for campgrounds, universities and apartment buildings",
        "body": [
          "Vutility makes smart submetering hardware that shows organizations how much energy their properties use. Its customers include campgrounds, universities, multifamily housing and commercial buildings.",
          "A single deal can cover far more than one site. Many buyers are investors or operating groups with whole portfolios of properties, so it helps to know who owns what before the first call."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "A clear market, and nobody left to do the prospecting",
        "body": [
          "Two people handled all of sales. When the marketing coordinator left, nobody was managing lists or running outreach. Earlier campaigns built from contact databases and industry associations had produced mixed results, and a trial of AI calling raised quality concerns.",
          "The website was drawing interest, but the team couldn’t tell which organizations were visiting or which ones mattered. Vutility needed prospecting to keep going without the two sellers doing all the research and outreach themselves."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Website signals plus human SDRs on the phones",
        "body": [
          "Visitor ID showed which companies were on the site, along with whatever contact details were available. A Demandbase integration and a score based on how recently and how often a company visited told the team where to look first. During rollout, one energy-sector company scored above 95.",
          "MarketBetter’s managed SDRs worked inside the platform, using scripts written around Vutility’s submetering pitch. The plan called for 50–100 calls and 50–100 emails a day, four days a week.",
          "When a prospect was interested, the SDRs introduced them straight to the founders. In the first month, an investment firm that manages more than 300 RV parks replied asking to talk further, a single conversation that could span an entire portfolio."
        ],
        "pullquote": "MarketBetter handled the sourcing, the outreach, the content — even our cookie-compliance fix. Our two-person team just had to show up for the meetings.",
        "pullquoteBy": "Eddie Meyersick · VP of Sales, Vutility"
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "A cookie fix that brought visitor tracking back",
        "body": [
          "Partway through, a cookie-consent problem broke part of the website and took visitor tracking down with it. The team removed the scripts causing it and fixed the setup, which brought visitor tracking back online so company identification could keep running."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Sellers get on calls already knowing the prospect",
        "body": [
          "The SDRs took on the recurring research and outreach. When a seller got on a call with the owner of a campground portfolio, they could talk about that operator’s energy needs and how many properties might be involved.",
          "Website visits showed the team which companies were showing interest. With only two people, that helped them decide where to spend their time, while the SDRs kept outreach going between meetings."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "Prospecting that keeps running for a two-person team",
        "body": [
          "Over roughly twelve months, MarketBetter identified **more than 2,000 ICP companies** visiting Vutility’s website. The SDRs made 493 calls in their first active month.",
          "The conversation with the private-equity firm showed the size of customer this work can reach. Email, calling and LinkedIn outreach keep going alongside it.",
          "Vutility’s sellers spend their time where their product and customer knowledge counts, and finding new prospects runs as a regular process in the background."
        ]
      }
    ],
    "hidden": false
  },
  "flolive": {
    "slug": "flolive",
    "line": "sales",
    "customer": "floLIVE",
    "industry": "IoT Connectivity SaaS",
    "cardTitle": "floLIVE found 171 target accounts on its website and new prospects every week",
    "stat": "171",
    "statLabel": "core accounts tracked from day one",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "floLIVE found 171 target accounts on its website and new prospects every week",
    "headlinePost": "",
    "dek": "floLIVE’s SDRs couldn’t see which companies were researching them, and finding new device makers took separate digging. MarketBetter now flags visiting accounts and newly certified devices and turns them into tasks. The rollout surfaced 171 ICP accounts and kept existing customers out of cold outreach.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "The number one priority for platform success is that workflows must be easily manageable by SDRs without extensive setup time. That's the bar we needed to clear — and MarketBetter cleared it.",
    "quoteAuthor": "Anna Vainer",
    "quoteTitle": "Director of Marketing, floLIVE",
    "quoteInitials": "AV",
    "quoteImage": "/images/testimonials/anna_vainer.jpeg",
    "statBadge": {
      "value": "171",
      "label": "accounts in pipeline from signals"
    },
    "results": [
      {
        "value": "171",
        "label": "core ICP accounts identified and tracked via Visitor ID",
        "accent": true
      },
      {
        "value": "326",
        "label": "Salesforce accounts matched and excluded from cold outreach"
      },
      {
        "value": "90%+",
        "label": "ICP-fit accuracy on AI-scored visitor tasks"
      },
      {
        "value": "Weekly",
        "label": "automated certification scans surfacing net-new device-maker prospects"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "floLIVE"
      ],
      [
        "Industry",
        "IoT Connectivity SaaS"
      ],
      [
        "Team",
        "2 SDRs, Marketing + Sales Ops"
      ],
      [
        "Using",
        "Visitor ID, Signals, SDR Playbooks, CRM Integration"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "A narrow market of companies building connected products",
        "body": [
          "floLIVE sells global IoT connectivity to device manufacturers, mobile operators and platform companies. The market is specialized. The accounts worth calling are the ones that actually need connectivity for something they’re building.",
          "Buyers often spend a while comparing coverage and pricing before they talk to sales. A manufacturer’s device development and certification can also hint at when it’s worth a closer look."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Plenty of data, and no clear next account",
        "body": [
          "The team already had Salesforce, HubSpot, ZoomInfo and Apollo. SDRs were still building campaigns by hand, and they had little idea which organizations were reading floLIVE’s website.",
          "They also wanted to find manufacturers that weren’t on any existing list. A newly certified device can mean the timing is right to reach out, but tracking certifications was its own research project."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Website visits and device certifications feed the SDRs",
        "body": [
          "MarketBetter’s website script identifies the companies that visit, and Slack notifications tell the SDR team when a target account shows up. In the first rollout, that surfaced 171 core ICP accounts that had been browsing the site unseen.",
          "Signals add company news, hiring and job changes to the account research.",
          "A weekly scan of the certification registry gives the team a second source of new accounts. Newly certified device makers are checked against Salesforce, and relevant ones outside the customer base become outreach tasks.",
          "SDRs also prospect in Sales Navigator and get researched email drafts. Visitor data, email drafts and call recordings all flow back to the CRM, so the research sits on the account record."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Every outreach starts with a reason",
        "body": [
          "A website visit and a device certification open different conversations. A visit tells the SDR what the account is looking at. A certification lets them talk about the manufacturer’s new connected product. MarketBetter puts both in front of the rep before they reach out.",
          "CRM matching happens in the same step. During the rollout, 326 existing accounts were matched and kept out of cold prospecting, so reps spent less time checking lists and more time on accounts that were actually new."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "171 accounts found in the rollout, and new ones every week",
        "body": [
          "The first deployment showed the team **171 ICP accounts** it hadn’t been able to see. Account scoring was over 90% accurate at judging whether a visitor was worth outreach, which helped SDRs decide where to spend their time.",
          "The weekly certification scan reaches companies that never visit the website. Between the two, the team gets a steady supply of new accounts and tasks ready to work.",
          "floLIVE renewed in 2026. The team has marked deeper CRM automation, LinkedIn audience targeting and lookalike prospecting as the next places to expand."
        ]
      }
    ],
    "hidden": false
  },
  "aidkit": {
    "slug": "aidkit",
    "line": "marketing",
    "customer": "AidKit",
    "industry": "GovTech / Public Benefits",
    "cardTitle": "How AidKit turns conference lists and disaster-funding news into outreach",
    "stat": "1,800+",
    "statLabel": "intent signals captured in first 90 days",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "How AidKit turns conference lists and disaster-funding news into outreach",
    "headlinePost": "",
    "dek": "AidKit sells to government agencies and nonprofits on long sales cycles, where timing and context matter. MarketBetter shows the team which organizations are on its website, researches conference attendees and drafts outreach tied to funding programs. It captured 1,800+ intent signals in the first 90 days.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "The question isn't whether to hire a GTM engineer — it's how to augment your existing team. What MarketBetter gives you is ideal leads at the ideal stage, in your inbox.",
    "quoteAuthor": "Hannah Burke",
    "quoteTitle": "AidKit",
    "quoteInitials": "HB",
    "quoteImage": "/images/testimonials/hannah_burke.png",
    "statBadge": {
      "value": "1,800+",
      "label": "intent signals, first 90 days"
    },
    "results": [
      {
        "value": "1,800+",
        "label": "intent signals captured in first 90 days of active use",
        "accent": true
      },
      {
        "value": "16",
        "label": "qualified disaster-recovery leads identified by AI in under 4 days"
      },
      {
        "value": "~325",
        "label": "conference attendees reached with AI-personalized emails before one event"
      },
      {
        "value": "1,000",
        "label": "conference attendees enriched from app screenshots for a single event"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "AidKit"
      ],
      [
        "Industry",
        "GovTech / Public Benefits"
      ],
      [
        "Team",
        "Marketing + Sales (6–8 users)"
      ],
      [
        "Using",
        "Visitor ID, Signals, Conference Pack, Emails, CRM Integration"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Selling benefits software to government",
        "body": [
          "AidKit helps government agencies and nonprofits run cash assistance and public benefits programs. Deals usually involve several stakeholders and take around two years to close.",
          "Disaster-recovery programs are the exception. They can move in about six months, so spotting the funding event and reaching the responsible organization quickly matters a great deal."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Too much research, too little follow-up",
        "body": [
          "Organizations browsed AidKit’s website without filling out a form, so the team rarely knew who was interested. An outside SDR agency worked static lists and got cold-call response rates around 5–7%.",
          "Events were the other big channel. AidKit attends and sponsors govtech conferences, but researching attendees took a lot of manual work. A strong attendee list didn’t turn into something an AE or SDR could actually work from."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Do the research before sales picks it up",
        "body": [
          "AidKit started with Visitor ID and Slack alerts for relevant website visits. Company identification, plus whatever person-level data was available, showed interest from government agencies, emergency-management organizations and nonprofits.",
          "MarketBetter scored that activity against AidKit’s ICP, checked contact emails and drafted sequences based on the pages people viewed and public company news. Existing customers were left out of outbound tasks.",
          "Conference Pack handled events. The team could upload an attendee CSV or pull names from screenshots of the conference app. Company research, recent news and conversation starters then came back in batches that took about five minutes."
        ]
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "Watching for disaster funding",
        "body": [
          "Weekly Market Intel scans tracked FEMA announcements, emergency declarations and disaster grants. When something relevant came up, an email draft tied to that specific program went to an SDR to review."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Reach people while the reason to call is fresh",
        "body": [
          "Before an event, an AE could use the enriched attendee list to write personalized outreach. After a funding announcement, an SDR could review an email about that program instead of opening with a generic introduction.",
          "The starting points differ, and the process stays the same. Marketing spots the event, organization or funding news. MarketBetter does the research. Sales reviews it and has the next conversation. CRM enrichment keeps ICP-matched website leads in that same flow."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "More signals, better-researched contacts, more event conversations",
        "body": [
          "MarketBetter captured **over 1,800 intent signals in AidKit’s first 90 days** of active use. The first disaster-recovery run found 16 qualified leads in under four days, each with a checked email address and a draft written around its context.",
          "At one conference, an AE reached about 325 attendees with a personalized two-touch sequence. At another, the team pulled 1,000 attendee records from conference-app screenshots and enriched them.",
          "The team no longer rebuilds its research from scratch for every event or funding program. When a timely audience comes up, the prep work is ready to use."
        ]
      }
    ],
    "featured": true,
    "hidden": false
  },
  "healthcare-it-leaders": {
    "slug": "healthcare-it-leaders",
    "line": "marketing",
    "customer": "Healthcare IT Leaders",
    "industry": "Healthcare IT Consulting",
    "cardTitle": "Healthcare IT Leaders hands five BDRs about 15 researched buyer signals a week",
    "stat": "~15",
    "statLabel": "prioritized prospect signals each week",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "Healthcare IT Leaders hands five BDRs about 15 researched buyer signals a week",
    "headlinePost": "",
    "dek": "Healthcare IT Leaders’ website draws health-system buyers and job seekers side by side. MarketBetter picks out the health systems doing research and gives five BDRs about 15 prioritized prospect signals each week, with enough account context to write personalized outreach.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "The lead information is very beneficial — it helps warm up outreach and gives us insight we would otherwise completely miss.",
    "quoteAuthor": "Courtney Driscoll",
    "quoteTitle": "VP Marketing, Healthcare IT Leaders",
    "quoteInitials": "CD",
    "quoteImage": "/images/testimonials/courtney_driscoll.jpeg",
    "statBadge": {
      "value": "~15",
      "label": "prioritized prospect signals each week"
    },
    "results": [
      {
        "value": "~15",
        "label": "high-intent prospect signals surfaced per week",
        "accent": true
      },
      {
        "value": "20%+",
        "label": "reply rates on signal-triggered personalized sequences"
      },
      {
        "value": "20K–40K",
        "label": "monthly website visitors, spanning buyers and job seekers"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "Healthcare IT Leaders"
      ],
      [
        "Industry",
        "Healthcare IT Consulting"
      ],
      [
        "Team",
        "Marketing + 5-person BDR team"
      ],
      [
        "Using",
        "Visitor ID, Signals"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Consultants for health-system technology projects",
        "body": [
          "Healthcare IT Leaders advises and staffs health systems through big technology programs, including Epic migrations, Oracle Health implementations and Workday rollouts.",
          "When the work started, the site drew 20,000 to 40,000 visitors a month. The buyers the team cared about were CIOs, IT directors and clinical application managers."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Buyers and job seekers on the same pages",
        "body": [
          "Plenty of that traffic came from people looking for work. The team had to tell a buyer researching consulting services apart from a candidate browsing open roles.",
          "Marketing couldn’t see which organizations were reading about consulting services unless someone filled out a form. That left BDRs with little to go on. They needed to know something about an account before they reached out."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Name the company and show what it was reading",
        "body": [
          "Visitor ID added the company name, visit history and the pages each visitor viewed. The team could now see which health systems were researching its services.",
          "In the first rollout, one academic medical center scored 98% on ICP fit. Fit scores told the team which visits to go after first.",
          "The Signals feed flagged roughly 15 priority visitors a week. Each alert came with whatever contact information was available, the pages viewed and a starting point for personalized outreach."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Give five BDRs something specific to say",
        "body": [
          "Five BDRs use that context to write personalized outreach. Four-touch sequences handle the follow-up and stop as soon as a prospect replies.",
          "If someone from a health system reads a service page, the rep has a relevant way to open. Account research fills in what the organization does and what it might need.",
          "Marketing decides which health systems to focus on, and sales works the conversations that come out of it."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "About 15 prioritized signals a week and a next step for ABM",
        "body": [
          "Healthcare IT Leaders reported about 15 prioritized prospect signals each week, each with account context the BDRs could use in outreach.",
          "The next planned step was a 5-to-10-account pilot of personalized landing pages, built on the account data marketing and sales already had.",
          "What those signals are worth in revenue depends on the conversations that follow."
        ]
      }
    ],
    "featured": true,
    "hidden": false
  },
  "connectbase": {
    "slug": "connectbase",
    "line": "sales",
    "customer": "Connectbase",
    "industry": "Telecom Connectivity Marketplace",
    "cardTitle": "Connectbase validated 43,000+ contacts and hit an 11.1% positive reply rate",
    "stat": "11.1%",
    "statLabel": "positive reply rate on AI-personalized outreach",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "Connectbase validated 43,000+ contacts and hit an 11.1% positive reply rate",
    "headlinePost": "",
    "dek": "Connectbase’s marketing team knew which companies and buyers it wanted. The hard part was getting sellers to work them consistently. MarketBetter validated 43,000+ contacts, built weekly audiences and prepared personalized campaigns for the North American and EMEA sales teams.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "Website traffic monitoring was too manual and job-change tracking just wasn't being done. MarketBetter closed that gap — now our reps have a prioritized task list every morning instead of starting from scratch.",
    "quoteAuthor": "Gabe Venturi",
    "quoteTitle": "Revenue Operations, Connectbase",
    "quoteInitials": "GV",
    "quoteImage": "/images/testimonials/gabe_venturi.jpeg",
    "statBadge": {
      "value": "11.1%",
      "label": "positive reply rate"
    },
    "results": [
      {
        "value": "43,000+",
        "label": "contacts verified and enriched",
        "accent": true
      },
      {
        "value": "11.1%",
        "label": "positive reply rate on AI outreach"
      },
      {
        "value": "120–150",
        "label": "LinkedIn connections/week per rep (platform max)"
      },
      {
        "value": "~1 hr",
        "label": "daily outreach completed per sales rep"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "Connectbase"
      ],
      [
        "Industry",
        "Telecom Connectivity Marketplace"
      ],
      [
        "Team",
        "Marketing + North American & EMEA Sales"
      ],
      [
        "Using",
        "Signals, Audiences, Emails, Dialer, CRM Integration"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "A telecom marketplace that asked its reps to prospect",
        "body": [
          "Connectbase runs a connectivity marketplace for the telecom industry. It sells across North America and EMEA, to several different kinds of buyers.",
          "As more of the prospecting fell to sales reps, the company needed a dependable way to give them good contacts, background on each target company and messaging that fit."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Tens of thousands of contact records nobody could trust",
        "body": [
          "More than 50,000 contact records needed checking. Reps had no reliable way to tell good records from bad ones, see which companies were on the website or notice changes at the companies they were chasing.",
          "Marketing was two people, already covering inquiries, campaigns and events. Building custom lists and research for every rep on top of that wasn’t realistic. Connectbase needed one preparation process that every seller could run with."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Fix the contact data first",
        "body": [
          "The rollout started with the data. MarketBetter checked each record for email deliverability, added LinkedIn details and sorted contacts into five buyer personas.",
          "With the personas in place, the team could write outreach that fit each buyer. Reps got personalized email sequences and could prospect on LinkedIn through the Chrome extension, working the same contacts marketing had prepared."
        ],
        "pullquote": "Our reps can knock out their entire day's outreach in about an hour. The platform handles the research, writes the email, verifies the address, and logs everything back to the CRM automatically.",
        "pullquoteBy": "Mary Ann Rose · VP Marketing, Connectbase"
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "Marketing builds the week’s campaign once",
        "body": [
          "Each week, marketing put together contact batches and messaging for each segment. Reps worked from that shared audience instead of rebuilding their own lists, and followed up by email, LinkedIn and phone.",
          "Signals added website visits, intent scores and ready-to-send email tasks to each rep’s day, with company details and, where available, the person behind the visit. Everything synced to the CRM, so the campaign history stayed visible to the whole team."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Marketing picks the targets, reps have the conversations",
        "body": [
          "The split was simple. Marketing chose the segments and wrote the message. Reps got researched contacts and a sequence ready to use. Website visits, company news and job changes gave them more reasons to reach out.",
          "Every morning the task list laid out the work. Reps said they could finish their outreach in about an hour, which left more of the day for discovery calls and open deals. Nobody had to build a separate setup of their own to get there."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "Better contact data and an 11.1% positive reply rate",
        "body": [
          "AI-personalized outreach earned an **11.1% positive reply rate**. The data project sent more than 43,000 validated, segmented contacts back to the CRM, giving the team much better coverage of the companies it sells to.",
          "During rollout, each rep added 120–150 new LinkedIn connections a week. That number measures activity. The reply rate measures how buyers responded.",
          "When Connectbase acquired CloudAge, the same enrichment process gave the team a ready way to bring a new set of contacts on board. The lasting change is a weekly handoff: marketing prepares, and sellers act on it."
        ]
      }
    ],
    "hidden": false
  },
  "allspice": {
    "slug": "allspice",
    "line": "sales",
    "hidden": false,
    "customer": "AllSpice",
    "industry": "Hardware Design Software",
    "cardTitle": "How AllSpice found the right hardware engineers and built $130K+ in opportunities",
    "stat": "$130K+",
    "statLabel": "across opportunities",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "How AllSpice found the right hardware engineers and built $130K+ in opportunities",
    "headlinePost": "",
    "dek": "AllSpice sells to small engineering teams inside large companies, so outreach has to reach the right few people with a real reason to talk. MarketBetter handles account research, intent signals and personalized outreach for its BDRs. AllSpice reports over $130K across opportunities and a Fortune 10 customer win.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "",
    "quoteAuthor": "",
    "quoteTitle": "",
    "quoteInitials": "",
    "statBadge": {
      "value": "$130K+",
      "label": "across opportunities"
    },
    "results": [
      {
        "value": "$130K+",
        "label": "across opportunities",
        "accent": true
      },
      {
        "value": "Fortune 10",
        "label": "customer win"
      },
      {
        "value": "235+",
        "label": "website visitors captured during initial activation"
      },
      {
        "value": "Engineering",
        "label": "account research and outreach tailored to hardware buyers"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "AllSpice"
      ],
      [
        "Industry",
        "Hardware Design Software"
      ],
      [
        "Sales leader",
        "Cuong, Head of Sales"
      ],
      [
        "Using",
        "Account research, Visitor ID, Signals, Audiences, Emails, CRM integration"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Collaboration software for hardware design teams",
        "body": [
          "AllSpice makes collaboration software for hardware and PCB design teams. Its buyers are electrical-engineering managers and senior leaders at consumer technology, networking, autonomous-vehicle and industrial companies.",
          "Even at a large enterprise, the group that matters may be a small engineering team. Selling well means understanding how that team designs hardware and finding the right people on it."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "A $20M pipeline goal and prep work scattered across tools",
        "body": [
          "AllSpice started with about $4.2M in pipeline, most of it from inbound, and set a target of $20M a year. Two BDRs were covering inbound demand and the first outbound efforts.",
          "Their prep work was split across Apollo, Bird Dog, HubSpot and manual LinkedIn activity. Hitting the target meant researching and contacting engineering accounts far more consistently. More contacts alone wouldn’t help. Each buyer needed a reason to care."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "CRM, research and signals in one place",
        "body": [
          "The rollout connected HubSpot to MarketBetter, along with the BDRs’ Gmail and Calendar accounts. Website tracking picked up more than 235 visitors early on, and fit scores showed which of them mattered.",
          "Signals covered hardware-engineering job postings, company news and relevant community mentions. Account size decided routing, so the right rep got the account as a task.",
          "To build new audiences, BDRs could pull Sales Navigator research into MarketBetter through the Chrome extension, or describe the audience they wanted in plain language. MarketBetter then researched each prospect’s company website and LinkedIn profile and wrote personalized email sequences."
        ]
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "Adding LinkedIn, closed-lost accounts and call follow-up",
        "body": [
          "The team added LinkedIn connection requests and follow-ups next to email, giving each campaign a second way to reach the people it picked.",
          "HubSpot filters helped the team go back to dormant closed-lost accounts. Call recordings from Fathom fed follow-up tasks, so the work after a buyer conversation ran through the same system as prospecting."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Each BDR gets an account with the homework done",
        "body": [
          "What the BDR receives is an account with context: what the company’s engineers work on, the right contact and a drafted message. The rep reviews it and opens a conversation about how that team collaborates on hardware design.",
          "For Cuong, AllSpice’s Head of Sales, it means the whole team prepares and works enterprise accounts the same way, while HubSpot still holds the CRM record."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "Over $130K across opportunities and a Fortune 10 win",
        "body": [
          "AllSpice reports **more than $130K across opportunities** and a **Fortune 10 customer win**. These are separate results. The $130K+ is opportunity value, and the Fortune 10 deal isn’t counted in it.",
          "The 235+ visitors captured at the start were the first account data to come in. From there, signals, research and ready-made campaigns gave the BDRs a clear way to work the engineering accounts that mattered.",
          "The $20M annual pipeline target is still a company goal. The opportunity total and the enterprise win are the results AllSpice reports so far."
        ]
      }
    ]
  },
  "machinecdn": {
    "slug": "machinecdn",
    "line": "sales",
    "customer": "MachineCDN",
    "industry": "Industrial IoT and Predictive Maintenance",
    "cardTitle": "MachineCDN closed $250K+ in sales by taking researched accounts to LinkedIn and the phone",
    "stat": "$250K+",
    "statLabel": "in closed sales, all through LinkedIn and phone",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "MachineCDN closed $250K+ in sales by taking researched accounts to LinkedIn and the phone",
    "headlinePost": "",
    "dek": "MachineCDN helps manufacturers cut machine downtime with sensors, cellular connectivity and AI that predicts failures. Plant leaders rarely answer cold email. VP of Sales Mike Dennis had his team work MarketBetter's account signals and research on LinkedIn and the phone instead, and closed more than $250K in sales.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "Plant managers don't answer cold email. MarketBetter told us which manufacturers were looking at us and what they cared about, so my team could pick up the phone and have a real conversation. That's how we closed over $250K.",
    "quoteAuthor": "Mike Dennis",
    "quoteTitle": "VP of Sales, MachineCDN",
    "quoteInitials": "MD",
    "quoteImage": "/images/testimonials/mike_dennis.jpeg",
    "statBadge": {
      "value": "$250K+",
      "label": "closed through LinkedIn and phone"
    },
    "results": [
      {
        "value": "$250K+",
        "label": "in closed sales, every deal worked through LinkedIn and phone",
        "accent": true
      },
      {
        "value": "286",
        "label": "companies identified visiting the MachineCDN website"
      },
      {
        "value": "40+",
        "label": "visiting accounts flagged as worth a sales conversation"
      },
      {
        "value": "19",
        "label": "trade podcast mentions tracked as account signals"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "MachineCDN"
      ],
      [
        "Industry",
        "Industrial IoT and Predictive Maintenance"
      ],
      [
        "Team",
        "Sales team led by VP of Sales Mike Dennis"
      ],
      [
        "Using",
        "Visitor ID, Signals, Audiences, Account research"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Machine monitoring for plants that can't afford downtime",
        "body": [
          "MachineCDN connects factory machines and uses AI to spot problems before they stop production. Sensors track vibration and temperature, cellular connectivity bypasses the plant network so IT doesn't have to get involved, and customers pay per monitored machine.",
          "Many of its buyers run plastics and packaging plants. For them, an unplanned stop on a single machine can hold up an order, so predictive maintenance is an easy idea to like and a hard purchase to prioritize."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "The right buyers don't live in their inbox",
        "body": [
          "MachineCDN sells to plant managers, maintenance leads and operations executives. They spend their days on the floor, and cold email wasn't getting their attention.",
          "Mike Dennis, MachineCDN's VP of Sales, wanted his team spending its time on the conversations that actually move a plant toward a decision. To do that, reps needed to know which manufacturers to go after, who to talk to there, and what to say when they got them on the phone."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Find the plants showing interest and research them first",
        "body": [
          "MarketBetter identified 286 companies visiting the MachineCDN website and scored each one for fit. Most were not buyers, and those were set aside. More than 40 were flagged as worth a sales conversation.",
          "The team also built a Sales Navigator list of contacts at plastics manufacturers, and MarketBetter tracked trade podcast episodes that mentioned relevant companies and topics, 19 of them so far. For each account, MarketBetter pulled together what the company makes and who is likely to own the decision, so a rep could start the conversation already knowing the plant."
        ]
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "Go where plant leaders actually respond",
        "body": [
          "Rather than add more email, Mike's team moved the work to two channels: LinkedIn and the phone. A rep would connect with the plant or operations leader on LinkedIn with a note about their operation, then follow up with a call.",
          "The research made those touches specific. A rep calling a packaging plant could talk about keeping its lines running, not read a generic pitch about IoT."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Every call starts with a reason",
        "body": [
          "Each day, reps worked accounts that had a reason to hear from MachineCDN: a recent website visit, a mention in a trade podcast, or a strong fit with the plastics and packaging plants MachineCDN already serves.",
          "Because the research was done before the call, reps spent their time talking with plant leaders instead of looking them up."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "More than $250K closed, without leaning on email",
        "body": [
          "MachineCDN has closed **more than $250K in sales** from this work. Every one of those deals came through LinkedIn and phone conversations.",
          "For a team selling to people who rarely read cold email, the lesson was simple. MarketBetter found the accounts and did the homework, and Mike's reps put their time into the two channels plant leaders actually answer."
        ]
      }
    ],
    "featured": false,
    "hidden": false
  },
  "soracom": {
    "slug": "soracom",
    "line": "marketing",
    "customer": "Soracom",
    "industry": "IoT Cellular Connectivity",
    "cardTitle": "Soracom found 106 outbound signals in website traffic it already had",
    "stat": "106",
    "statLabel": "qualified outbound signals surfaced",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "Soracom found 106 outbound signals in website traffic it already had",
    "headlinePost": "",
    "dek": "Soracom’s small sales team spent most of its time on inbound, with little left for outbound. MarketBetter identified the companies browsing the site and surfaced 106 outbound-ready signals from traffic Soracom was already getting.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "",
    "quoteAuthor": "Melissa Hohertz-Foat",
    "quoteTitle": "Global Head of Marketing, Soracom",
    "quoteInitials": "MH",
    "quoteImage": "/images/testimonials/melissa_hohertz_foat.jpeg",
    "statBadge": {
      "value": "106",
      "label": "qualified outbound signals surfaced"
    },
    "results": [
      {
        "value": "106",
        "label": "qualified outbound signals surfaced from existing inbound traffic",
        "accent": true
      },
      {
        "value": "70%",
        "label": "of website companies identified with verified contact data"
      },
      {
        "value": "7-person",
        "label": "sales team that now has a specific list of accounts to look into"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "Soracom"
      ],
      [
        "Industry",
        "IoT Cellular Connectivity"
      ],
      [
        "Team",
        "7-person sales org"
      ],
      [
        "Using",
        "Visitor ID, Signals, CRM Integration"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "IoT connectivity for enterprises in the US and UK",
        "body": [
          "Soracom sells IoT and cellular connectivity to enterprises. Its seven-person sales organization depended heavily on website inquiries and partner channels, with two SDRs handling that demand.",
          "The US and UK teams both needed a way to spot real business buyers and enough background on each one to follow up well."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "No time and no visibility for outbound",
        "body": [
          "Inbound questions and partner support filled the SDRs’ days, and many of those questions came from consumers Soracom couldn’t sell to.",
          "Marketing couldn’t see which companies were researching the site unless a visitor got in touch. With the SDRs tied up, there was little time to look into those accounts or start any outbound."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Identify the companies already on the site",
        "body": [
          "Visitor ID showed which companies were on the site. The team could see which pages an organization viewed, whether several people from the same company were active, and which campaign brought them in.",
          "That made it easier to tell a one-off visit from interest across a whole account."
        ]
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "A prospecting list from existing traffic",
        "body": [
          "Once Signals was turned on, it surfaced 106 outbound-ready prospects from existing traffic. These were people showing relevant interest who had never turned into inbound leads.",
          "At one large industrial technology account, website activity also gave the team context on a sales opportunity already in progress.",
          "IoT news monitoring added another possible reason to reach out, and the team looked at the dialer as an addition to its calling."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Helping reps decide which accounts come first",
        "body": [
          "Account identification brought a new group into view: business buyers who were researching the site without ever getting in touch.",
          "For those accounts, a seller could check page activity, company fit and existing CRM ownership before reaching out. Soracom then started testing a fuller outbound program with CRM integration, built on the account context it had gathered from its website traffic."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "Outbound prospects from demand it already had",
        "body": [
          "MarketBetter surfaced **106 qualified outbound signals** in Soracom’s existing website traffic. A team that had spent its time on inbound now had a specific list of accounts to look into.",
          "Visitor identification matched 70% of visiting companies with verified contact data.",
          "Account signals now give sellers a starting point for proactive follow-up."
        ]
      }
    ],
    "hidden": false
  },
  "parks-associates": {
    "slug": "parks-associates",
    "line": "marketing",
    "customer": "Parks Associates",
    "industry": "IoT & Smart Home Research",
    "cardTitle": "Parks Associates swapped hand-built emails for personalized outreach to 377 energy-sector contacts",
    "stat": "377",
    "statLabel": "contacts activated with personalized outreach",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "Parks Associates swapped hand-built emails for personalized outreach to 377 energy-sector contacts",
    "headlinePost": "",
    "dek": "Parks Associates’ research on connected technology pulls buyers to its website. MarketBetter shows the team which organizations are reading it and helps write outreach around that interest, including personalized campaigns to a 377-contact energy audience.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "The layout is comparable to ZoomInfo and Apollo, but it actually tells me who's on our site and what they looked at — that changes what I say in my first email entirely.",
    "quoteAuthor": "Rob Gallagher",
    "quoteTitle": "Sales Development Representative, Parks Associates",
    "quoteInitials": "RG",
    "quoteImage": "/images/testimonials/rob_gallagher.png",
    "statBadge": {
      "value": "377",
      "label": "energy-sector contacts reached"
    },
    "results": [
      {
        "value": "377",
        "label": "energy-sector contacts activated with AI outreach",
        "accent": true
      },
      {
        "value": "Real-time",
        "label": "visitor ID with intent scoring"
      },
      {
        "value": "By topic",
        "label": "which research drew each account in, visible before the first email"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "Parks Associates"
      ],
      [
        "Industry",
        "IoT & Smart Home Research"
      ],
      [
        "Team",
        "Sales & Research"
      ],
      [
        "Using",
        "Visitor ID, Signals, Emails"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Research reports that bring specialist buyers to the site",
        "body": [
          "Parks Associates publishes research on IoT, smart homes and connected consumer technology. Companies read its reports for market insight that shapes their own product and business decisions.",
          "For the sales team, what an account reads says a lot about what it needs. An energy company, for example, may read a different set of reports from a company sizing up the broader smart-home market."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "A contact list with no sense of what anyone was reading",
        "body": [
          "The energy outreach program ran off a 377-contact list in ACT. Engagement tracking was thin and every email was written by hand, so it was hard to tie an account’s interest to a specific report or topic.",
          "Sellers needed to see which accounts mattered and have a clear handoff when someone showed interest in the research."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Let what an account reads shape the first email",
        "body": [
          "MarketBetter pulled visiting companies, pages viewed, visit frequency and referral sources into one view. Where individual visitors could be identified, it added their contact details too. Sellers could see which research topics had drawn each organization in.",
          "For selected audiences, MarketBetter used company research to draft personalized emails. A public filing or a product-fit detail could give the rep an opening tied to the buyer’s business, which says far more than a job title."
        ],
        "pullquote": "I used to build every outreach email by hand in ACT. Now I know which report someone read before I write the first line.",
        "pullquoteBy": "Rob Gallagher · SDR, Parks Associates"
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "Put the research library in reps’ hands",
        "body": [
          "A Microsoft Teams assistant gave the internal team access to Parks Associates’ research library. Reps could pull report material and talking points without leaving the tools they already used, which made it easier to carry an account’s topic of interest into a follow-up."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Write the email around what the account is reading",
        "body": [
          "Reps now start with something specific: the company, the topic it has been reading about and research relevant to its business. They can pair a chosen contact list with that context when building a campaign.",
          "The 377-contact energy audience put this to work. MarketBetter prepared company-specific outreach for that segment, cutting the manual research needed to explain why each recipient should care."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "377 energy contacts reached with personalized outreach",
        "body": [
          "Parks Associates sent personalized outreach to **377 energy-sector contacts**. Account research and report engagement gave sellers a better basis for the first message.",
          "Marketing’s research already held the context. Now the sales team has practical ways to use it in conversations with accounts."
        ]
      }
    ],
    "hidden": false
  },
  "qx-global-group": {
    "slug": "qx-global-group",
    "line": "sales",
    "hidden": false,
    "customer": "QX Global Group",
    "industry": "Business Process Outsourcing",
    "cardTitle": "QX Global Group researches each target company before the first sales email goes out",
    "stat": "162",
    "statLabel": "contacts enriched in the initial pilot audience",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "QX Global Group researches each target company before the first sales email goes out",
    "headlinePost": "",
    "dek": "QX sells into small, hand-picked groups of companies in specialized industries. MarketBetter helped turn those lists into enriched audiences and personalized email sequences, starting with 162 Senior Living contacts and later adding event and regional finance-buyer campaigns.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "",
    "quoteAuthor": "",
    "quoteTitle": "",
    "quoteInitials": "",
    "statBadge": {
      "value": "162",
      "label": "contacts enriched in the initial pilot audience"
    },
    "results": [
      {
        "value": "162",
        "label": "contacts enriched in the initial Senior Living audience",
        "accent": true
      },
      {
        "value": "4-touch",
        "label": "personalized sequence prepared during the pilot"
      },
      {
        "value": "4",
        "label": "buyer personas generated during setup"
      },
      {
        "value": "325",
        "label": "recipients in September 2026 outreach records"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "QX Global Group"
      ],
      [
        "Industry",
        "Business Process Outsourcing"
      ],
      [
        "Team",
        "US Growth, Marketing & Sales"
      ],
      [
        "Using",
        "Audiences, Signals, Emails"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "A short list of named companies in each vertical",
        "body": [
          "QX Global Group is a business-process management company headquartered in the UK, with about 3,000 employees. It works in accounting, real estate, staffing and insurance.",
          "In the US, QX targets roughly 100–200 named companies in each vertical. With a list that short, every conversation counts, and the research has to reflect how each buyer’s business runs and what they are responsible for."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "A good list, and generic messaging that could waste it",
        "body": [
          "QX had to tell apart buyer groups that look alike on paper, such as finance leaders at multifamily housing companies and finance leaders in other residential markets. Broad intent categories and generic emails tend to blur that line.",
          "Event outreach was already working, with 25–30% connection rates before MarketBetter came in. The team wanted the same quality of preparation for its other campaigns, without needing a dedicated marketing-operations person to do it."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "A pilot built on a real list",
        "body": [
          "QX tried MarketBetter in a hands-on pilot. During onboarding, the team uploaded a CSV of 162 Senior Living contacts, filled in missing website information and mapped a custom field. MarketBetter generated four buyer personas from what QX sells.",
          "The campaign paired a research agent with a four-touch email sequence. Before writing anything, the agent looked at each company for likely finance and accounting problems: a slow close process, complicated reporting, heavy AP/AR work, staffing pressure. The opening lines drew on what it found."
        ]
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "One brief behind every campaign",
        "body": [
          "The team set up AI Context with QX’s target markets, use cases and answers to common objections. Every email draft and every fit check drew on that same brief, so different campaigns described the business the same way.",
          "Website identification and community monitoring flagged interested companies alongside the planned lists, so the team could see which named accounts were already paying attention."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Event and vertical campaigns in the hands of sales",
        "body": [
          "In the pilot, one salesperson’s target accounts were matched with an event audience. Marketing owned the ICP definitions and the data. The seller worked the researched campaign.",
          "By September 2026, campaign records showed event and regional finance-buyer outreach, with 456 sent or replied email entries across 325 recipients. Those numbers count activity. They are not revenue."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "A campaign process that fits a narrow market",
        "body": [
          "The first campaign combined **162 enriched contacts**, four personas and a four-touch sequence personalized to each company. QX could test its chosen audience with research and outreach handled from start to finish.",
          "Later campaigns took the same approach to 325 recipients across event and regional audiences. Each audience kept its own context while using the same research and sending tools.",
          "For QX, the vertical strategy now turns directly into seller activity. Marketing picks the companies and writes the context. Sales gets researched campaigns ready to work."
        ]
      }
    ]
  },
  "levelblue": {
    "slug": "levelblue",
    "line": "sales",
    "customer": "LevelBlue",
    "industry": "Cybersecurity / Managed Security",
    "cardTitle": "LevelBlue showed sellers which named accounts were researching it and built $1.7M+ in pipeline in one quarter",
    "stat": "$1.7M+",
    "statLabel": "pipeline from 18 new opportunities in one quarter",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "LevelBlue showed sellers which named accounts were researching it and built $1.7M+ in pipeline in one quarter",
    "headlinePost": "",
    "dek": "LevelBlue’s direct and channel sellers couldn’t see which named accounts were researching them. MarketBetter matched the visiting companies against LevelBlue’s named-account list, put a sales assistant inside Microsoft Teams and ran AI-based outreach. LevelBlue created 18 new opportunities worth more than $1.7M in pipeline in one quarter.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "For the first time, I can see the named accounts quietly evaluating us — who they are, what they're reading, and how they found us — and I can hand a seller a reason to reach out before our competitors even know the deal exists. That's not a report anymore. That's pipeline.",
    "quoteAuthor": "Denise O'Donovan",
    "quoteTitle": "Director of Marketing, LevelBlue",
    "quoteInitials": "DO",
    "quoteImage": "/images/testimonials/denise_odonovan.jpeg",
    "statBadge": {
      "value": "$1.7M+",
      "label": "pipeline in one quarter"
    },
    "results": [
      {
        "value": "$1.7M+",
        "label": "pipeline from 18 new opportunities in a single quarter",
        "accent": true
      },
      {
        "value": "18",
        "label": "new opportunities generated in a single quarter"
      },
      {
        "value": "Company-level",
        "label": "intent identification on named accounts that used to visit anonymously"
      },
      {
        "value": "Microsoft Teams",
        "label": "a single AI sales assistant trained on Binder and the full website"
      },
      {
        "value": "ZoomInfo",
        "label": "persona-matched prospect lookup wired in for one-click outreach"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "LevelBlue"
      ],
      [
        "Industry",
        "Cybersecurity / Managed Security"
      ],
      [
        "Team",
        "Sales, Channel & Marketing"
      ],
      [
        "Using",
        "Signals, Visitor ID, AI Sales Assistant (Teams), Personas, CRM Integration"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Security services sold to enterprises and through partners",
        "body": [
          "LevelBlue sells cybersecurity services and technology: managed detection and response, network security, consulting and its USM platform. It sells to enterprise buyers directly and through managed security service provider partners.",
          "The two groups read different parts of the website. Product pages tell an enterprise seller what an account cares about, while partner-program research matters more to a channel account manager."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Named accounts were reading the site, and sellers couldn’t see them",
        "body": [
          "Only a small share of visitors ever filled out a form. Marketing was bringing in the right organizations, but sellers had no view of who they were or what they were reading.",
          "Preparing for an account also meant checking several places. Sales-enablement material sat in Binder, and account history and other context lived elsewhere. The team needed that information within easy reach and a way to know when a target account was showing interest."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Named-account activity shows up where sellers already work",
        "body": [
          "LevelBlue uploaded its named-account list. MarketBetter identified the companies visiting the site and scored their engagement by recency and number of distinct visitors. Several people from one account reading network-security pages, for example, could flag it for a closer look. Referral source and geography sat right next to the activity.",
          "Among the accounts that surfaced were a large equipment-rental company with visits from several locations and a managed security partner reading up on the channel program. Persona definitions and ZoomInfo lookup helped find the right contacts.",
          "At the same time, LevelBlue set up a Microsoft Teams assistant trained on Binder material and its public website. Sellers could ask it which accounts had visited, or ask product questions, without leaving the tool they already used."
        ],
        "pullquote": "Our sellers don't want one more dashboard to check — they want the right account, the right contact, and a draft ready to send. Putting it inside Teams is what makes them actually use it.",
        "pullquoteBy": "Jenny Galinski · Marketing, LevelBlue"
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "Direct and channel sellers working from the same view",
        "body": [
          "The channel team could use partner-program activity to prepare for a conversation with an MSSP. Direct sellers could see which topics an enterprise account was researching and pull up the sales material to match.",
          "The next phase was planned to tie intent thresholds to alerts for the assigned seller, contact lookup and Outreach sequences, with Eloqua and Salesforce sync on the same roadmap. Those pieces would add to the account intelligence and Teams assistant already in place."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Sellers ask a question and get the account context",
        "body": [
          "A seller can start by asking whether a named account has visited recently and what it looked at. The assistant pulls that together with product and pricing information, so the seller does less digging.",
          "Direct buyers and partners still get different conversations. The same visitor data supports both, and what the seller says depends on the account’s relationship with LevelBlue and the pages it’s reading."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "18 new opportunities in a quarter, and a clear view of which named accounts are visiting",
        "body": [
          "With named-account intent in front of sellers and AI-based outreach running, LevelBlue generated **18 new opportunities worth more than $1.7M in pipeline in a single quarter**.",
          "Within weeks of turning on tracking, named-account visits that used to be anonymous showed up with stakeholder, referral and location details. Sellers could dig into that interest through the Teams assistant, with enablement material close at hand.",
          "Marketing now sees which accounts are engaging, and sales has a practical way to prepare. The planned CRM and outreach integrations would build on that."
        ],
        "pullquote": "Signals surfaced the accounts showing real intent — now our reps know exactly where to aim.",
        "pullquoteBy": "Dan Ghillone · AVP, LevelBlue"
      }
    ],
    "hidden": false
  },
  "apx-net": {
    "slug": "apx-net",
    "line": "marketing",
    "customer": "APX Net",
    "industry": "Broadband ISP",
    "cardTitle": "How APX Net turned a new direct-sales plan into live outreach",
    "stat": "205",
    "statLabel": "emails sent in July–August 2026",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "How APX Net turned a new direct-sales plan into live outreach",
    "headlinePost": "",
    "dek": "APX Net has a channel business and wanted to add direct sales. MarketBetter helped it identify the companies on its website, define the buyers it wanted and prepare outreach. Later campaigns sent 205 emails to 10G and IT infrastructure buyers.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "The visitor data gives us insights that Google Analytics stopped offering years ago. Now I can actually see which companies are researching us and why they scored high — it's a completely different starting point for outreach.",
    "quoteAuthor": "Karen Mathis",
    "quoteTitle": "Marketing, APX Net",
    "quoteInitials": "KM",
    "quoteImage": "/images/testimonials/karen_mathis.jpeg",
    "statBadge": {
      "value": "205",
      "label": "emails sent in July–August 2026"
    },
    "results": [
      {
        "value": "205",
        "label": "emails sent in July–August 2026",
        "accent": true
      },
      {
        "value": "54",
        "label": "AI-scored prospect signals in the original rollout"
      },
      {
        "value": "Direct sales",
        "label": "account audiences for 10G and IT infrastructure buyers"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "APX Net"
      ],
      [
        "Industry",
        "Broadband ISP"
      ],
      [
        "Team",
        "Lean marketing team, no dedicated SDR"
      ],
      [
        "Using",
        "Visitor ID, Signals"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Adding direct sales to a channel business",
        "body": [
          "APX Net provides enterprise connectivity in a market where channel partners carry a lot of weight. The company wanted a direct route to buyers as well.",
          "When the work started, marketing was getting ready to hire a dedicated direct-sales rep. The first job was to build the account research and prospecting process that person would use."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Website interest stopped at the analytics report",
        "body": [
          "Visitors read connectivity guides and contact pages, but the team couldn’t tell which organizations they came from or whom to approach. Website analytics showed activity without the context a sales conversation needs.",
          "Prospecting by hand on LinkedIn meant a lot of guesswork with little to go on. Before APX Net could keep direct outreach running, it had to define its buyers, recognize the organizations that matched and research the contacts."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Define the buyers, then rank the accounts",
        "body": [
          "MarketBetter’s tracking script turned on visitor identification, and Signals scored accounts against APX Net’s ICP. The early rollout produced 54 scored prospect signals, each with an explanation of why it was relevant.",
          "The team defined two to three personas, including network managers and administrators in telecom and broadband. Those personas filtered the website activity, and built-in enrichment supplied contact details for the accounts that made the cut."
        ],
        "pullquote": "The AI does first-pass filtering so only qualified leads appear in my task queue — companies that don't fit our profile are excluded before I ever see them.",
        "pullquoteBy": "Karen Mathis · Marketing, APX Net"
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "Getting ready ahead of the sales hire",
        "body": [
          "Marketing used those scored accounts and personas to set up outreach tools ahead of the dedicated hire. With the account queue and contact data already in one place, one marketing generalist could handle the preparation while the company built up its direct-sales capacity."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Emailing 10G and IT infrastructure buyers",
        "body": [
          "Later campaigns put that preparation to work. APX Net published audiences of 10G and IT infrastructure buyers and sent 205 emails to 205 recipients between July 23 and August 26, 2026.",
          "That is the step after identifying accounts: pick the audience, prepare the outreach, send it. The 205 figure counts emails sent. It does not mean every recipient became a meeting or an opportunity."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "From account research to live campaigns",
        "body": [
          "In its first rollout, APX Net set up visitor identification, a scored account queue and buyer personas, all before its direct-sales hire was in place.",
          "The **54 initial prospect signals** gave sales specific accounts to work on. Built-in enrichment also cut the need for a separate process to find contact data.",
          "The later **205-email campaign cohort** shows that groundwork carrying through to live outreach. APX Net now has a practical way to move the account segments it picked into everyday sales work."
        ]
      }
    ],
    "hidden": false
  },
  "bluerock": {
    "slug": "bluerock",
    "line": "sales",
    "hidden": false,
    "customer": "BlueRock",
    "industry": "AI Software",
    "cardTitle": "BlueRock created $90K+ in pipeline in 2026 by giving technical buyers useful content first",
    "stat": "$90K+",
    "statLabel": "USD pipeline created in 2026",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "BlueRock created $90K+ in pipeline in 2026 by giving technical buyers useful content first",
    "headlinePost": "",
    "dek": "BlueRock wanted to reach technical buyers with content worth reading and outreach that fit. MarketBetter handled account research and campaign prep for SDR Jake Anton, helping create over $90K USD in pipeline during 2026.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "",
    "quoteAuthor": "",
    "quoteTitle": "",
    "quoteInitials": "",
    "statBadge": {
      "value": "$90K+",
      "label": "USD pipeline created in 2026"
    },
    "results": [
      {
        "value": "$90K+",
        "label": "USD pipeline created in 2026",
        "accent": true
      },
      {
        "value": "Jake Anton",
        "label": "SDR working the account motion"
      },
      {
        "value": "54",
        "label": "visiting companies identified in the original campaign cycle"
      },
      {
        "value": "33",
        "label": "signal-based action items generated in the original campaign cycle"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "BlueRock"
      ],
      [
        "Industry",
        "AI Software"
      ],
      [
        "Sales execution",
        "Jake Anton, SDR"
      ],
      [
        "Using",
        "Signals, Audiences, account research and personalized email"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "A small team selling AI software to technical buyers",
        "body": [
          "BlueRock is an AI software company that sells to technical decision-makers. It builds interest with educational resources, including white papers and open-source material.",
          "When an account executive left, the small team needed a way to keep outbound going and still answer inbound interest. Nobody had time to run a separate operations function."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Good content only works if the right people see it",
        "body": [
          "The team wanted to open with technical value instead of making every first email a meeting request. That took research, because the content had to fit the recipient’s role and what the account cared about.",
          "BlueRock also needed website identification, signal monitoring and CRM connections working alongside personalized email. If those pieces didn’t fit together, a lean team would spend most of its time preparing activity and little of it talking to buyers."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Set up personas, research and campaigns together",
        "body": [
          "During onboarding, BlueRock connected its email, calendar and CRM and defined four buyer personas: AI product, platform reliability, security governance and engineering leadership.",
          "Website identification and signals came next, including hiring activity and community keywords. The team could import existing audiences from the CRM to start choosing accounts.",
          "The campaign builder used company and LinkedIn research to draft personalized emails built around technical white papers and an open-source repository. The team reviewed and edited the messages before they went out, keeping the content at the center of every email."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Give Jake the context for technical conversations",
        "body": [
          "Jake Anton ran the outreach as BlueRock’s SDR. His campaigns went to technical audiences around MCP communities and events, using useful resources as the reason to get in touch.",
          "MarketBetter did the prep behind it: choosing relevant accounts, researching each recipient and drafting a message with context. Jake then worked the conversations that came back. A security company was one of the accounts in the resulting pipeline."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "Over $90K USD in pipeline created during 2026",
        "body": [
          "MarketBetter helped BlueRock create **over $90,000 USD in pipeline in 2026**. The figure counts new pipeline and includes work with a security-company account.",
          "The first account-discovery cycle identified 54 visiting companies and produced 33 action items from website visits, hiring, news and community activity. That gave a small team specific accounts to look into and a reason to look at each one.",
          "BlueRock kept its focus on technical content throughout. Account research matched the material to the right buyers, and Jake’s follow-up turned the resulting conversations into pipeline."
        ]
      }
    ]
  },
  "ramen": {
    "slug": "ramen",
    "line": "sales",
    "customer": "Ramen",
    "industry": "Industrial Networking",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "quote": "I can focus on selling instead of prospecting.",
    "quoteAuthor": "Jarrett Steiner",
    "quoteTitle": "Sales, Ramen",
    "quoteInitials": "JS",
    "atAGlance": [
      [
        "Company",
        "Ramen"
      ],
      [
        "Industry",
        "Industrial networking"
      ],
      [
        "Sales perspective",
        "Jarrett Steiner"
      ],
      [
        "Account use case",
        "Warehouse and outdoor-yard modernization"
      ]
    ],
    "cardTitle": "Ramen built pipeline with a warehouse operator that wanted product tracking without trenching",
    "headlinePre": "",
    "headlineHi": "Ramen built pipeline with a warehouse operator that wanted product tracking without trenching",
    "headlinePost": "",
    "dek": "Ramen sells industrial connectivity and operational visibility. MarketBetter helped it create pipeline with a warehouse and outdoor-yard operator that wanted to track inbound and outbound products with RANO and EdgeAI vision, without trenching.",
    "date": "September 2026",
    "readTime": "3 min",
    "hidden": false,
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Networking and vision for industrial sites",
        "body": [
          "Ramen offers Autonomous Network Operations (RANO) and EdgeAI vision. Industrial customers use them to tie network infrastructure to the work happening across a physical site.",
          "One opportunity came from a warehouse and outdoor-yard operator that wanted a better way to track products coming in and going out. That project put Ramen’s networking and vision products to work at a specific site."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Tracking products without digging up the site",
        "body": [
          "Industrial buyers start with the job they want done. For this operator, that job spans a warehouse and an outdoor yard, with products moving in and out of the site.",
          "The operator wanted to see those movements without trenching or rewiring the site. Ramen’s pitch had to show how its technology could work within that limit."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Build the pitch around the warehouse and the yard",
        "body": [
          "MarketBetter helped Ramen create pipeline for this operator. The product conversation centered on RANO and EdgeAI vision, applied to the operator’s need to track inbound and outbound products.",
          "That gave the sales effort a clear focus. The buyer’s site, the way products move through it and the no-trenching constraint explain why the project matters to them. They also give Ramen a concrete way to talk about how it can help."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "More time with the customer, less on prospecting",
        "body": [
          "For Jarrett Steiner, the payoff is time to sell. MarketBetter helps with the prospecting that comes before a customer conversation, so sales has more room to dig into what the buyer needs.",
          "The warehouse opportunity gave the team something concrete to talk through: where products enter and leave, how the outdoor yard connects to the warehouse, and how RANO and EdgeAI vision could handle tracking without trenching or rewiring."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "Pipeline from a specific industrial problem",
        "body": [
          "MarketBetter helped Ramen create pipeline with a warehouse and outdoor-yard operator. The opportunity rests on a clear operational need: tracking inbound and outbound products across the site.",
          "Because the need is specific, the sales team had a solid place to start. They could discuss RANO and EdgeAI vision as part of modernizing the warehouse, with the operator’s installation limits shaping the conversation."
        ]
      }
    ],
    "stat": "Pipeline",
    "statLabel": "created for an industrial account",
    "statBadge": {
      "value": "Pipeline",
      "label": "created for an industrial account"
    },
    "results": [
      {
        "value": "Pipeline",
        "label": "created for a warehouse and yard account",
        "accent": true
      },
      {
        "value": "RANO",
        "label": "Autonomous Network Operations use case"
      },
      {
        "value": "EdgeAI",
        "label": "vision for inbound and outbound product tracking"
      }
    ]
  },
  "gxc": {
    "slug": "gxc",
    "line": "sales",
    "customer": "GXC",
    "industry": "Private Cellular Networks",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "quote": "",
    "quoteAuthor": "",
    "quoteTitle": "",
    "quoteInitials": "",
    "atAGlance": [
      [
        "Company",
        "GXC"
      ],
      [
        "Industry",
        "Private Cellular Networks"
      ],
      [
        "Use case",
        "Route relevant website interest into a seller conversation"
      ]
    ],
    "cardTitle": "GXC doubled the marketing-qualified leads coming from its website",
    "headlinePre": "",
    "headlineHi": "GXC doubled the marketing-qualified leads coming from its website",
    "headlinePost": "",
    "dek": "GXC’s website drew people researching private cellular networks, but those visits didn’t reliably reach a salesperson. MarketBetter added personalized engagement on the site and routed interested visitors to sales through the CRM.",
    "date": "September 2026",
    "readTime": "3 min",
    "hidden": true,
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Private cellular networks for enterprise connectivity",
        "body": [
          "GXC provides private cellular networks to organizations that need enterprise connectivity. Its website draws visitors from around the world who want to know how a private network could work in their operations.",
          "Mostly, the site was a place to read. GXC wanted the right visits to turn into real conversations with its sales team."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Interested visitors, and no one assigned to respond",
        "body": [
          "The team had no consistent way to engage the right visitors while they were on the site. Its existing tools were hard to integrate and support, and interest on the website didn’t reliably reach a sales rep.",
          "A private-network buyer’s first question is often part technical and part business case. For the handoff to be useful, sales needs to know what the organization is exploring."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Sort the traffic, start a conversation, assign an owner",
        "body": [
          "MarketBetter brought together website-traffic segmentation, personalized engagement and CRM integration. Segmentation pointed the team to the visitors who mattered most.",
          "On relevant pages, visitors got tailored interactions that invited them to start a conversation. The CRM integration then assigned each one to a sales rep, so every conversation had an owner for follow-up.",
          "Marketing’s website activity now fed straight into sales. What the team learned about the account during that engagement went with it, ready for the rep’s next conversation."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "The right rep picks up with context",
        "body": [
          "The idea is simple: connect a private-network buyer who fits with the seller who can help. The website conversation supplies the context, and routing makes it clear who should respond.",
          "Less lead handling happens by hand. Reps pick up the inquiry already knowing what came up on the website and carry the discussion forward around the buyer’s project."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "Twice as many website marketing-qualified leads",
        "body": [
          "GXC doubled its website marketing-qualified leads during the engagement. Personalized interactions gave the right visitors a reason to speak up, and CRM routing put those inquiries in front of sales.",
          "The gains went past capturing leads. Segmentation helped the team decide what to prioritize, and reps got the context they needed to follow up with private-network buyers."
        ]
      }
    ],
    "stat": "2×",
    "statLabel": "website marketing-qualified leads",
    "statBadge": {
      "value": "2×",
      "label": "website marketing-qualified leads"
    },
    "results": [
      {
        "value": "2×",
        "label": "website marketing-qualified leads",
        "accent": true
      },
      {
        "value": "CRM routing",
        "label": "visitor engagement assigned to sales"
      },
      {
        "value": "Private networks",
        "label": "account context for technical sales conversations"
      }
    ],
    "featured": false
  },
  "twnex": {
    "slug": "twnex",
    "line": "sales",
    "customer": "TWNEX",
    "industry": "Channel Management Software",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "quote": "",
    "quoteAuthor": "",
    "quoteTitle": "",
    "quoteInitials": "",
    "atAGlance": [
      [
        "Company",
        "TWNEX"
      ],
      [
        "Industry",
        "Channel Management Software"
      ],
      [
        "Use case",
        "Give channel teams context for relevant partner conversations"
      ]
    ],
    "cardTitle": "How TWNEX spots interested partners and follows up with context",
    "headlinePre": "",
    "headlineHi": "How TWNEX spots interested partners and follows up with context",
    "headlinePost": "",
    "dek": "TWNEX sells channel management software, so its most important relationships are with partners. MarketBetter shows the channel team which partners are visiting the website and carries that context into their follow-up.",
    "date": "September 2026",
    "readTime": "3 min",
    "hidden": true,
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Channel software for technology companies",
        "body": [
          "TWNEX makes channel management software for technology companies. Partner relationships sit at the center of its business, so engaging valuable channel partners is a top commercial priority.",
          "TWNEX uses MarketBetter to connect website activity to the people who own those relationships. When the channel team knows a partner is interested, it has a reason and some context for reaching out."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Partner interest was hard to see and slow to act on",
        "body": [
          "With tools that didn’t talk to each other, the team was slow to respond to online interest. A high-value partner could visit the website and leave the channel team with too little to start a useful conversation.",
          "Handling leads by hand also made it harder to tie each interaction back to the system of record. The team needed to see relevant interest and have a simple way to carry it into partner follow-up."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Visitor identification, chat and CRM working together",
        "body": [
          "MarketBetter added website visitor identification and a chatbot. Identification showed the team which relevant organizations were on the site, and the chatbot let visitors start a conversation right away.",
          "CRM integration connected that engagement to the team’s systems, so what was learned on the site could inform the relationship work that followed.",
          "Together they cover two steps: knowing a partner is interested, and getting the team ready to do something about it."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Open the partner conversation with something relevant",
        "body": [
          "For a channel team, the next step might be a conversation about the partnership or about the services the partner was researching. Knowing what the account looked at gives the seller a better opener than a generic check-in.",
          "By tying online interactions to follow-up, TWNEX improved partner engagement and cut manual work. The relationship owner could use the account’s interests to steer the next conversation."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "A clear line from partner interest to channel-team follow-up",
        "body": [
          "TWNEX improved partner engagement and handled leads more efficiently. Visitor identification and chatbot conversations showed the team who was interested, and CRM integration carried that context into follow-up.",
          "The channel team now has a practical way to act on its account priorities: see which organization is interested, understand what it engaged with and prepare the relationship owner for the next conversation."
        ]
      }
    ],
    "stat": "Partners",
    "statLabel": "account context for channel follow-up",
    "statBadge": {
      "value": "Partners",
      "label": "account context for channel follow-up"
    },
    "results": [
      {
        "value": "Partner accounts",
        "label": "context for channel relationship development",
        "accent": true
      },
      {
        "value": "Visitor ID",
        "label": "organization-level context for follow-up"
      },
      {
        "value": "CRM integration",
        "label": "engagement connected to the team’s workflow"
      }
    ],
    "featured": false
  },
  "helix-wireless": {
    "slug": "helix-wireless",
    "line": "sales",
    "customer": "Helix Wireless",
    "industry": "IoT Connectivity",
    "cardTitle": "Helix Wireless found 26 high-intent accounts among 2,599 companies visiting its site",
    "stat": "26",
    "statLabel": "high-intent accounts surfaced from website traffic",
    "readTime": "3 min",
    "headlinePre": "",
    "headlineHi": "Helix Wireless found 26 high-intent accounts among 2,599 companies visiting its site",
    "headlinePost": "",
    "dek": "Helix Wireless sells SmartSIM connectivity and MVNE services to companies building connected products and mobile networks. Its website drew visitors from thousands of companies, and almost none of them said who they were. MarketBetter identified 2,599 of those companies, set aside the ones that didn't fit, and put 26 high-intent accounts in front of Helix's sellers.",
    "author": "Wendy Gonzalez",
    "authorTitle": "Customer Stories, MarketBetter",
    "authorInitials": "WG",
    "authorImage": "/images/testimonials/wendy_gonzalez.png",
    "date": "September 2026",
    "quote": "We cut follow-up time from days to minutes — growing 2× faster.",
    "quoteAuthor": "Francis Gallic",
    "quoteTitle": "CEO, Helix Wireless",
    "quoteInitials": "FG",
    "quoteImage": "https://assets.marketbetter.ai/profiles/francis-gallic.jpeg",
    "statBadge": {
      "value": "26",
      "label": "high-intent accounts from website traffic"
    },
    "results": [
      {
        "value": "26",
        "label": "high-intent accounts flagged for sellers",
        "accent": true
      },
      {
        "value": "2,599",
        "label": "companies identified visiting the Helix website"
      },
      {
        "value": "898",
        "label": "accounts kept on a watch list instead of worked cold"
      },
      {
        "value": "500",
        "label": "signals routed to 4 sellers with the reason to reach out"
      }
    ],
    "atAGlance": [
      [
        "Company",
        "Helix Wireless"
      ],
      [
        "Industry",
        "IoT Connectivity"
      ],
      [
        "Team",
        "4 sellers working routed account signals"
      ],
      [
        "Using",
        "Visitor ID, Signals, Account research"
      ]
    ],
    "sections": [
      {
        "id": "about",
        "kicker": "The company",
        "title": "Connectivity for companies building their own networks and devices",
        "body": [
          "Helix Wireless provides SmartSIM connectivity, connectivity as a service, and Mobile Virtual Network Enabler (MVNE) services. Its buyers range from companies putting cellular connections into their own devices to operators launching mobile networks under their own brand.",
          "That makes for a wide market. A health-tech company monitoring patients at home and a security-focused MVNO both need what Helix sells, but they come to it with very different questions."
        ]
      },
      {
        "id": "challenge",
        "kicker": "The challenge",
        "title": "Thousands of visitors, and no way to tell who was buying",
        "body": [
          "Helix's website pulled in visits from all over the world. Most of those companies never filled out a form, so the sales team couldn't tell a serious buyer from a student, a competitor or a job seeker.",
          "Working that traffic by hand wasn't realistic. The team needed to know which companies fit, which ones were actually researching Helix, and what they were looking at before anyone reached out."
        ]
      },
      {
        "id": "solution",
        "kicker": "Our approach",
        "title": "Identify every visiting company, then score what it did on the site",
        "body": [
          "Between December 2025 and August 2026, MarketBetter identified 2,599 companies behind 3,418 visits to the Helix website. Each visit was scored for fit and intent using the company's business, the pages it read and how often it came back.",
          "Most of the traffic didn't fit, and MarketBetter set about 1,664 of those companies aside so no one wasted a call on them. It kept 898 accounts on a watch list to track over time, and flagged 26 as high-intent."
        ]
      },
      {
        "id": "activation",
        "kicker": "Putting it into practice",
        "title": "What high intent looked like at Helix",
        "body": [
          "The high-intent accounts had clear patterns. Many returned several times over a few days. Many went straight to the MVNE services page, others to SmartSIM, and several ended up on the partner and contact pages.",
          "They also looked like Helix's best buyers: mobile virtual network operators, remote patient monitoring companies, aerospace and defense firms, and an established paging network operator. Some visits came from Singapore and Pakistan, a reminder that Helix's market isn't only in the US."
        ]
      },
      {
        "id": "sales-execution",
        "kicker": "Daily selling",
        "title": "Four sellers, each with a reason to reach out",
        "body": [
          "MarketBetter routed 500 signals to Helix's four sellers. Each one arrived with the company, the pages it read, how many times it came back and why it looked like a fit.",
          "That changes the first message. Instead of a generic introduction, a seller can open with MVNE for a company that just spent two days reading about it, or with SmartSIM for a device maker comparing connectivity options."
        ]
      },
      {
        "id": "results",
        "kicker": "The results",
        "title": "A short list worth calling, drawn from thousands of visits",
        "body": [
          "Helix now knows who is on its website. Out of **2,599 identified companies, 26 were flagged as high-intent**, and 898 more sit on a watch list until they show stronger interest.",
          "The sales team spends its time on accounts that are already researching MVNE and SmartSIM, with the context to make the first conversation specific."
        ]
      }
    ],
    "featured": false,
    "hidden": false
  }
};

/* ---- Derived views (single source of truth above) ----
   Hidden studies are excluded everywhere: listing grid, featured carousel,
   and detail routes (getStudy returns undefined → 404). */
export const ALL_STUDIES: CaseStudy[] = Object.values(CASE_STUDIES).filter((s) => !s.hidden);
export const FEATURED_STUDIES: CaseStudy[] = ALL_STUDIES.filter((s) => s.featured);
export function getStudy(slug: string): CaseStudy | undefined {
  const study = CASE_STUDIES[slug];
  return study && !study.hidden ? study : undefined;
}
